// Who this workspace has actually emailed from the platform. The chat agent
// run scopes its Gmail reads to these people — a raw Gmail feed is mostly
// newsletters and vendor mail that has nothing to do with outreach.
//
// One honest source, no guessing: `email_sent` events, logged by the Gmail
// send API when this app sends a message (ComposeDock.jsx is the only writer).
//
// "Outreach history" means WE ACTUALLY EMAILED THEM. It does NOT mean "an
// address we happen to have written down somewhere". That distinction is
// load-bearing in two places and getting it wrong is silently destructive:
//   • agentRun.js's reply freeze scopes its Gmail query through outreachQuery()
//     and Gmail caps query length, so only the first `cap` addresses survive.
//     Every address in here that we never emailed evicts a real counterparty,
//     and their reply never reaches the run.
//   • agentRun.js excludes this set from "leads to reach out to", so a padded
//     set makes the next run report untouched leads as "already contacted".
import { listEvents } from './events.js'

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi

function addressesIn(text) {
  return String(text || '').match(EMAIL_RE) || []
}

/**
 * Everyone this workspace has emailed.
 *
 * Never throws — the source is fault-tolerant — so degradation is reported
 * STRUCTURALLY instead. Callers that assert something about the history
 * ("0 already contacted") MUST look at `degraded` / `sources`: a rejected
 * promise is not available as a signal, because listEvents() swallows a dead
 * /api/events and falls back to this browser's local mirror (empty on a second
 * device).
 *
 * @returns {Promise<{
 *   addresses: Set<string>,   // lowercase; activity-log order — see ordering note
 *   sources: {
 *     sends: { ok: boolean, mode: 'api'|'local'|'unavailable', count: number },
 *   },
 *   degraded: boolean,        // true when the set is known to be incomplete
 * }>}
 *
 * ONE SOURCE, AND WHY THERE IS NO SECOND ONE. There used to be: the contacts
 * enrolled in a launched campaign or a sequence, which counted as outreach
 * because the schedule had already committed to writing to them. Campaigns and
 * sequences are gone, so nobody can be enrolled and there is nothing left to
 * read — the lane is impossible now, not empty, and a standing zero reported
 * from it would be a source that can never go dark pretending to be one that
 * did. `sources` stays a map rather than a bare flag for the same reason it
 * always was one: a caller has to be able to say WHICH lane it could not read.
 *
 * ORDERING NOTE (load-bearing, not cosmetic): a Set preserves insertion order,
 * and addresses go in in activity-log order, newest send first. outreachQuery()
 * truncates at `cap`, so that ordering is what keeps the most recent
 * counterparties inside the Gmail query when the workspace has more known
 * addresses than Gmail will take. Callers passing their own iterable should
 * order it the same way — whoever matters most, first.
 */
export async function knownRecipients({ eventLimit = 300 } = {}) {
  const addresses = new Set()
  let fromSends = 0

  // ── logged sends ──────────────────────────────────────────────────────────
  // listEvents() resolves even when /api/events is down: it returns mode
  // 'local' with whatever this browser mirrored. That is a legitimate answer
  // for rendering a feed and a WORTHLESS one for asserting "nobody has been
  // contacted", so only mode 'api' counts as a verified read.
  let sends = { ok: false, mode: 'unavailable', count: 0 }
  try {
    const res = await listEvents({ limit: eventLimit })
    const events = Array.isArray(res?.events) ? res.events : []
    const mode = res?.mode === 'api' ? 'api' : 'local'
    for (const e of events) {
      if (e.type !== 'email_sent') continue
      // "Sent “subject” to name@company.com" — a subject can itself contain an
      // address, so take the last one, which is the recipient.
      const found = addressesIn(e.detail)
      if (!found.length) continue
      const addr = found[found.length - 1].toLowerCase()
      if (!addresses.has(addr)) fromSends += 1
      addresses.add(addr)
    }
    sends = { ok: mode === 'api', mode, count: fromSends }
  } catch {
    // Should not happen (listEvents catches its own fetch failure), but if the
    // shape ever changes, report it as unread rather than as empty.
    sends = { ok: false, mode: 'unavailable', count: fromSends }
  }

  return {
    addresses,
    sources: { sends },
    degraded: !sends.ok,
  }
}

// Gmail query that keeps the feed to outreach counterparties. Gmail caps query
// length, so only the first `cap` addresses go in; the caller tells the user
// when that truncation happened rather than silently dropping people.
//
// "First" is not arbitrary: knownRecipients() yields addresses in activity-log
// order, so the ones that survive the cap are the people we most recently
// emailed. Callers passing their own iterable should order it the same way.
export function outreachQuery(addresses, { base = 'in:inbox', cap = 60 } = {}) {
  const all = [...addresses]
  const list = all.slice(0, cap)
  if (!list.length) return { q: '', used: 0, dropped: all.length }
  return {
    q: `${base} from:(${list.join(' OR ')})`,
    used: list.length,
    dropped: Math.max(0, all.length - list.length),
  }
}
